import { Anchor, Tooltip } from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { IconSearch } from "@tabler/icons-react";
import { useCallback } from "react";
import useUserStore from "@/modules/users/store";
import { captureException } from "@/services/log";
import { useCheckSimilarIdeas, SimilarIdea } from "../../hooks/useCheckSimilarIdeas";

interface CheckSimilarLinkProperties {
  titulo: string;
  descricao: string;
  disabled?: boolean;
  /**
   * Callback chamado quando ideias similares são encontradas.
   * O componente pai deve exibir um modal.
   */
  onSimilarIdeasFound?: (similarIdeas: SimilarIdea[]) => void;
}

const DISABLED_OPACITY = 0.5;

/**
 * Link para verificar ideias similares antes de salvar
 * Exibe um modal com as ideias encontradas ou uma notificação de sucesso
 * @param root0
 * @param root0.titulo
 * @param root0.descricao
 * @param root0.disabled
 * @param root0.onSimilarIdeasFound
 */
export function CheckSimilarLink({
  titulo,
  descricao,
  disabled = false,
  onSimilarIdeasFound
}: CheckSimilarLinkProperties) {
  const [activeLicenciadaId, activeEmpresaId] = useUserStore((state) => [
    state.activeLicenciadaId,
    state.activeEmpresaId
  ]);
  const { mutateAsync: checkSimilarIdeas, isPending } = useCheckSimilarIdeas();

  const checkSimilarity = useCallback(async () => {
    // Garantido pela validação em handleCheckSimilarity
    if (!activeEmpresaId) return;

    try {
      const similarIdeas = await checkSimilarIdeas({
        titulo,
        descricao,
        licenciadaId: activeLicenciadaId,
        empresaId: activeEmpresaId,
        minSimilarity: 0.7,
        maxResults: 5
      });

      if (!similarIdeas || similarIdeas.length === 0) {
        showNotification({
          title: "Nenhuma ideia similar encontrada",
          message: "Sua ideia parece ser única! Continue com o cadastro.",
          color: "green",
          autoClose: 3000
        });
        return;
      }

      if (onSimilarIdeasFound) {
        onSimilarIdeasFound(similarIdeas);
      } else {
        showNotification({
          title: "Ideias similares encontradas",
          message: `Encontramos ${similarIdeas.length} ${similarIdeas.length === 1 ? "ideia similar" : "ideias similares"}.`,
          color: "yellow",
          autoClose: 3000
        });
      }
    } catch (error) {
      captureException(error as Error, true);
      showNotification({
        title: "Erro ao verificar similaridade",
        message: "Não foi possível verificar ideias similares. Tente novamente.",
        color: "red",
        autoClose: 3000
      });
    }
  }, [titulo, descricao, activeLicenciadaId, activeEmpresaId, checkSimilarIdeas]);

  const handleCheckSimilarity = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();

      if (disabled || !titulo || !descricao || !activeEmpresaId) {
        return;
      }

      void checkSimilarity();
    },
    [disabled, titulo, descricao, activeEmpresaId, checkSimilarity]
  );

  const isDisabled = disabled || !titulo?.trim() || !descricao?.trim() || isPending;

  const tooltipLabel = isDisabled
    ? "Preencha o título e descrição para verificar"
    : "Clique para verificar se já existem ideias similares";

  return (
    <Tooltip label={tooltipLabel} position="top" withArrow>
      <Anchor
        size="sm"
        onClick={handleCheckSimilarity}
        style={{
          cursor: isDisabled ? "not-allowed" : "pointer",
          opacity: isDisabled ? DISABLED_OPACITY : 1,
          pointerEvents: isDisabled ? "none" : "auto",
          display: "inline-flex",
          alignItems: "center",
          gap: "4px"
        }}
        aria-label="Verificar se existem ideias similares"
        aria-disabled={isDisabled}
        tabIndex={isDisabled ? -1 : 0}
      >
        <IconSearch size={14} aria-hidden="true" />
        {isPending ? "Verificando..." : "Verificar similaridade"}
      </Anchor>
    </Tooltip>
  );
}

export default CheckSimilarLink;
